// frontend/src/pages/ReportsPage.jsx
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { fetchPoles, fetchProximityLogs } from "../services/api";

const BAR_COLORS = ["#4ade80","#3b82f6","#f59e0b","#8b5cf6","#f87171","#34d399"];

// Simple bar chart component
function BarChart({ data, maxVal }) {
  return (
    <div style={{ display:"flex", alignItems:"flex-end", gap:6, height:120, padding:"0 4px" }}>
      {data.map((item, i) => {
        const pct = maxVal > 0 ? (item.value / maxVal) * 100 : 0;
        const color = BAR_COLORS[i % BAR_COLORS.length];
        return (
          <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
            <div style={{ fontSize:10, color:"#94a3b8", fontWeight:700 }}>{item.value}</div>
            <div style={{ width:"100%", height:`${Math.max(pct,2)}%`, background:color, borderRadius:"4px 4px 0 0",
              border:`1px solid ${color}60`, transition:"height 0.5s ease", minHeight:4 }} />
            <div style={{ fontSize:9, color:"#4b5563", textAlign:"center", lineHeight:1.2, maxWidth:50, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{item.label}</div>
          </div>
        );
      })}
    </div>
  );
}

// Export to CSV
function exportCSV(logs) {
  const headers = ["Pole","Pole Code","User","Latitude","Longitude","Distance (m)","Date","Time"];
  const rows = logs.map(l => [
    l.pole_name || l.pole_id,
    l.pole_code || "",
    l.username  || "",
    l.latitude  || "",
    l.longitude || "",
    l.distance_m ? parseFloat(l.distance_m).toFixed(1) : "",
    l.logged_at ? new Date(l.logged_at).toLocaleDateString("en-LK") : "",
    l.logged_at ? new Date(l.logged_at).toLocaleTimeString("en-LK") : "",
  ]);
  const csv = [headers, ...rows].map(r => r.join(",")).join("\n");
  const blob = new Blob([csv], { type:"text/csv" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `proximity_logs_${new Date().toISOString().split("T")[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function ReportsPage() {
  const navigate    = useNavigate();
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");

  const [poles, setPoles]       = useState([]);
  const [logs, setLogs]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filterPole, setFilter] = useState("");
  const [filterDate, setDate]   = useState("");
  const [activeTab, setTab]     = useState("logs"); // logs | chart | summary

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      fetchPoles().then(r => setPoles(r.data.data)),
      fetchProximityLogs(null, 500).then(r => setLogs(r.data.data || [])),
    ]).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Filtered logs
  const filtered = logs.filter(l => {
    const matchPole = !filterPole || String(l.pole_id) === filterPole;
    const matchDate = !filterDate || (l.logged_at && l.logged_at.startsWith(filterDate));
    return matchPole && matchDate;
  });

  // ── Chart data — visits per pole
  const visitsByPole = poles.map(p => ({
    label: p.pole_code,
    value: logs.filter(l => String(l.pole_id) === String(p.id)).length,
  })).sort((a,b) => b.value - a.value);
  const maxVisits = Math.max(...visitsByPole.map(v => v.value), 1);

  // ── Daily summary — last 7 days
  const today = new Date();
  const last7 = Array.from({ length:7 }, (_, i) => {
    const d = new Date(today);
    d.setDate(d.getDate() - (6 - i));
    const key = d.toISOString().split("T")[0];
    return {
      label: d.toLocaleDateString("en-LK", { weekday:"short", month:"short", day:"numeric" }),
      key,
      value: logs.filter(l => l.logged_at && l.logged_at.startsWith(key)).length,
    };
  });
  const maxDay = Math.max(...last7.map(d => d.value), 1);

  const TABS = [
    { key:"logs",    label:"📋 Log Table" },
    { key:"chart",   label:"📊 Charts" },
    { key:"summary", label:"📅 Daily Summary" },
  ];

  return (
    <div style={{ minHeight:"100vh", background:"#0a0f1a", fontFamily:"'JetBrains Mono',monospace", color:"#e2e8f0" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;600;700&family=Syne:wght@700;800&display=swap');
        * { box-sizing:border-box; }
        select,input { background:#0a1628; border:1px solid #1e3a2e; border-radius:8px; padding:8px 12px; color:#e2e8f0; font-size:12px; outline:none; }
        @media(max-width:600px) { .top-bar{flex-wrap:wrap;gap:8px!important} .filter-bar{flex-wrap:wrap} }
      `}</style>

      {/* Top Nav */}
      <div className="top-bar" style={{ background:"linear-gradient(135deg,#0d2010,#0a1628)", borderBottom:"1px solid #1a2e1a",
        padding:"12px 20px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:12 }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <button onClick={() => navigate("/dashboard")}
            style={{ background:"transparent", border:"1px solid #1e3a2e", color:"#4ade80", borderRadius:8, padding:"6px 12px", fontSize:11, cursor:"pointer" }}>
            ← Dashboard
          </button>
          <div>
            <div style={{ fontFamily:"'Syne',sans-serif", fontSize:16, fontWeight:800, color:"#4ade80" }}>📊 Reports</div>
            <div style={{ fontSize:9, color:"#4b5563", letterSpacing:"0.12em" }}>PROXIMITY LOGS & ANALYTICS</div>
          </div>
        </div>
        <button onClick={() => exportCSV(filtered)}
          style={{ background:"#14532d", border:"1px solid #22c55e", color:"#4ade80", borderRadius:8, padding:"7px 14px", fontSize:11, fontWeight:700, cursor:"pointer" }}>
          ⬇️ Export CSV
        </button>
      </div>

      {/* Stats bar */}
      <div style={{ background:"#0d1520", borderBottom:"1px solid #1a2e1a", padding:"12px 20px", display:"flex", gap:20, overflowX:"auto" }}>
        {[
          { label:"Total Logs",   value: logs.length,                     color:"#4ade80" },
          { label:"Today",        value: logs.filter(l => l.logged_at?.startsWith(today.toISOString().split("T")[0])).length, color:"#3b82f6" },
          { label:"This Week",    value: last7.reduce((a,d) => a+d.value, 0), color:"#f59e0b" },
          { label:"Poles Visited",value: new Set(logs.map(l=>l.pole_id)).size, color:"#8b5cf6" },
        ].map(s => (
          <div key={s.label} style={{ textAlign:"center", minWidth:70 }}>
            <div style={{ fontSize:20, fontWeight:800, color:s.color, fontFamily:"'Syne',sans-serif" }}>{s.value}</div>
            <div style={{ fontSize:10, color:"#4b5563" }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tab bar */}
      <div style={{ background:"#0d1520", borderBottom:"1px solid #1a2e1a", padding:"0 20px", display:"flex" }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{ background:"transparent", border:"none", borderBottom:`2px solid ${activeTab===t.key ? "#4ade80" : "transparent"}`,
              color: activeTab===t.key ? "#4ade80" : "#64748b", padding:"12px 16px", fontSize:12,
              fontWeight: activeTab===t.key ? 700 : 400, cursor:"pointer", fontFamily:"inherit" }}>
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ maxWidth:1100, margin:"0 auto", padding:"20px 16px" }}>

        {/* ── LOG TABLE TAB ── */}
        {activeTab === "logs" && (
          <div>
            {/* Filters */}
            <div className="filter-bar" style={{ display:"flex", gap:10, marginBottom:16, flexWrap:"wrap" }}>
              <select value={filterPole} onChange={e=>setFilter(e.target.value)} style={{ flex:1, minWidth:150 }}>
                <option value="">All Poles</option>
                {poles.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <input type="date" value={filterDate} onChange={e=>setDate(e.target.value)} style={{ flex:1, minWidth:140 }} />
              <button onClick={() => { setFilter(""); setDate(""); }}
                style={{ background:"#1e2d3d", border:"1px solid #1e3a2e", color:"#94a3b8", borderRadius:8, padding:"8px 14px", fontSize:11, cursor:"pointer" }}>
                Clear
              </button>
              <div style={{ fontSize:11, color:"#4b5563", display:"flex", alignItems:"center" }}>
                {filtered.length} records
              </div>
            </div>

            {/* Table */}
            <div style={{ background:"#0d1520", border:"1px solid #1a2e1a", borderRadius:14, overflow:"hidden" }}>
              <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead>
                    <tr style={{ background:"#060c12" }}>
                      {["#","Pole","Pole Code","User","Distance","GPS Location","Date & Time"].map(h => (
                        <th key={h} style={{ padding:"10px 14px", fontSize:10, color:"#4b5563", textAlign:"left", letterSpacing:"0.08em", borderBottom:"1px solid #1a2e1a", whiteSpace:"nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={7} style={{ padding:32, textAlign:"center", color:"#4b5563", fontSize:12 }}>Loading...</td></tr>
                    ) : filtered.length === 0 ? (
                      <tr><td colSpan={7} style={{ padding:32, textAlign:"center", color:"#4b5563", fontSize:12 }}>No records found</td></tr>
                    ) : filtered.map((log, i) => (
                      <tr key={i} style={{ background: i%2===0 ? "rgba(255,255,255,0.01)" : "transparent" }}>
                        <td style={{ padding:"8px 14px", fontSize:11, color:"#4b5563" }}>{i+1}</td>
                        <td style={{ padding:"8px 14px", fontSize:12, color:"#4ade80", fontWeight:600, whiteSpace:"nowrap" }}>{log.pole_name || `Pole #${log.pole_id}`}</td>
                        <td style={{ padding:"8px 14px", fontSize:11, color:"#64748b" }}>{log.pole_code || "—"}</td>
                        <td style={{ padding:"8px 14px", fontSize:12, color:"#94a3b8" }}>{log.username || "—"}</td>
                        <td style={{ padding:"8px 14px", fontSize:12, color:"#e2e8f0" }}>{log.distance_m ? `${parseFloat(log.distance_m).toFixed(1)}m` : "—"}</td>
                        <td style={{ padding:"8px 14px", fontSize:11, color:"#4b5563", whiteSpace:"nowrap" }}>
                          {log.latitude ? `${parseFloat(log.latitude).toFixed(6)}, ${parseFloat(log.longitude).toFixed(6)}` : "—"}
                        </td>
                        <td style={{ padding:"8px 14px", fontSize:11, color:"#64748b", whiteSpace:"nowrap" }}>
                          {log.logged_at ? new Date(log.logged_at).toLocaleString("en-LK", { month:"short", day:"numeric", year:"numeric", hour:"2-digit", minute:"2-digit" }) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ── CHART TAB ── */}
        {activeTab === "chart" && (
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
            {/* Visits per pole */}
            <div style={{ background:"#0d1520", border:"1px solid #1a2e1a", borderRadius:14, padding:20 }}>
              <div style={{ fontSize:13, fontWeight:700, color:"#e2e8f0", marginBottom:4 }}>Visits Per Pole</div>
              <div style={{ fontSize:11, color:"#4b5563", marginBottom:16 }}>Total proximity logs per pole</div>
              {loading ? <div style={{ color:"#4b5563", textAlign:"center", padding:40 }}>Loading...</div>
                : <BarChart data={visitsByPole} maxVal={maxVisits} />}
            </div>

            {/* Status distribution */}
            <div style={{ background:"#0d1520", border:"1px solid #1a2e1a", borderRadius:14, padding:20 }}>
              <div style={{ fontSize:13, fontWeight:700, color:"#e2e8f0", marginBottom:4 }}>Pole Status Distribution</div>
              <div style={{ fontSize:11, color:"#4b5563", marginBottom:16 }}>Current status of all poles</div>
              {loading ? <div style={{ color:"#4b5563", textAlign:"center", padding:40 }}>Loading...</div> : (
                <div>
                  {[
                    { status:"Active",      color:"#22c55e" },
                    { status:"Maintenance", color:"#f59e0b" },
                    { status:"Inactive",    color:"#6b7280" },
                    { status:"Pending",     color:"#3b82f6" },
                  ].map(s => {
                    const count = poles.filter(p => p.status === s.status).length;
                    const pct   = poles.length > 0 ? (count / poles.length) * 100 : 0;
                    return (
                      <div key={s.status} style={{ marginBottom:12 }}>
                        <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:4 }}>
                          <span style={{ color: s.color }}>{s.status}</span>
                          <span style={{ color:"#94a3b8" }}>{count} poles ({pct.toFixed(0)}%)</span>
                        </div>
                        <div style={{ background:"#0a1628", borderRadius:99, height:8, overflow:"hidden" }}>
                          <div style={{ width:`${pct}%`, height:"100%", background:s.color, borderRadius:99, transition:"width 0.5s ease" }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Most visited */}
            <div style={{ background:"#0d1520", border:"1px solid #1a2e1a", borderRadius:14, padding:20, gridColumn:"1/-1" }}>
              <div style={{ fontSize:13, fontWeight:700, color:"#e2e8f0", marginBottom:4 }}>Most Visited Poles</div>
              <div style={{ fontSize:11, color:"#4b5563", marginBottom:16 }}>Ranked by number of proximity log entries</div>
              {loading ? <div style={{ color:"#4b5563", textAlign:"center", padding:20 }}>Loading...</div> : (
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {visitsByPole.map((v, i) => {
                    const pct = maxVisits > 0 ? (v.value / maxVisits) * 100 : 0;
                    const color = BAR_COLORS[i % BAR_COLORS.length];
                    return (
                      <div key={i} style={{ display:"flex", alignItems:"center", gap:12 }}>
                        <div style={{ fontSize:12, color:"#4b5563", width:20, textAlign:"right" }}>#{i+1}</div>
                        <div style={{ fontSize:12, color:"#e2e8f0", width:60, fontWeight:600 }}>{v.label}</div>
                        <div style={{ flex:1, background:"#0a1628", borderRadius:99, height:10, overflow:"hidden" }}>
                          <div style={{ width:`${pct}%`, height:"100%", background:color, borderRadius:99, transition:"width 0.6s ease" }} />
                        </div>
                        <div style={{ fontSize:12, color, fontWeight:700, width:40, textAlign:"right" }}>{v.value}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── DAILY SUMMARY TAB ── */}
        {activeTab === "summary" && (
          <div>
            {/* Last 7 days chart */}
            <div style={{ background:"#0d1520", border:"1px solid #1a2e1a", borderRadius:14, padding:20, marginBottom:16 }}>
              <div style={{ fontSize:13, fontWeight:700, color:"#e2e8f0", marginBottom:4 }}>Last 7 Days Activity</div>
              <div style={{ fontSize:11, color:"#4b5563", marginBottom:16 }}>Number of proximity events per day</div>
              {loading ? <div style={{ color:"#4b5563", textAlign:"center", padding:40 }}>Loading...</div>
                : <BarChart data={last7.map(d=>({ label: d.label.split(",")[0], value: d.value }))} maxVal={maxDay} />}
            </div>

            {/* Daily breakdown table */}
            <div style={{ background:"#0d1520", border:"1px solid #1a2e1a", borderRadius:14, overflow:"hidden" }}>
              <div style={{ padding:"14px 20px", borderBottom:"1px solid #1a2e1a", fontSize:13, fontWeight:700, color:"#e2e8f0" }}>
                Daily Breakdown
              </div>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead>
                  <tr style={{ background:"#060c12" }}>
                    {["Date","Day","Events","Poles Visited","Unique Users"].map(h => (
                      <th key={h} style={{ padding:"10px 16px", fontSize:10, color:"#4b5563", textAlign:"left", letterSpacing:"0.08em", borderBottom:"1px solid #1a2e1a" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {last7.slice().reverse().map((day, i) => {
                    const dayLogs    = logs.filter(l => l.logged_at?.startsWith(day.key));
                    const uniqPoles  = new Set(dayLogs.map(l=>l.pole_id)).size;
                    const uniqUsers  = new Set(dayLogs.map(l=>l.username)).size;
                    return (
                      <tr key={i} style={{ background: i%2===0 ? "rgba(255,255,255,0.01)" : "transparent" }}>
                        <td style={{ padding:"10px 16px", fontSize:12, color:"#e2e8f0" }}>{day.key}</td>
                        <td style={{ padding:"10px 16px", fontSize:12, color:"#94a3b8" }}>{day.label}</td>
                        <td style={{ padding:"10px 16px", fontSize:12, color: day.value > 0 ? "#4ade80" : "#4b5563", fontWeight: day.value > 0 ? 700 : 400 }}>{day.value}</td>
                        <td style={{ padding:"10px 16px", fontSize:12, color:"#94a3b8" }}>{uniqPoles}</td>
                        <td style={{ padding:"10px 16px", fontSize:12, color:"#94a3b8" }}>{uniqUsers}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
